// src/checklistLogAdmin/index.js — the admin checklist + store-log template
// editors, submissions/history views, demo-seed and retention-purge (moved
// VERBATIM from admin.html in four ranges, FE-breakup slice 6; bundled to
// dist/checklistLogAdmin.js).
//
// PAGE-GLOBAL STATE CONTRACT (deliberate — declarations stay in admin.html):
//   clTemplates, clTplEditing, logTemplatesAdmin, logEntriesAdmin,
//   logTplEditing — read AND WRITTEN cross-bundle by src/uploads/checklists.js,
//   src/uploads/logs.js and the staff-side src/checklists bundle. Bare-name
//   access from here resolves to the same page binding (the documented
//   classic-script pattern); moving the declarations would fork the state.
//   v104-1-bare-references_test pins "let clTemplates = [];" in admin.html.
//   escapeHtml/escapeAttr + the compliance state block stay inline too.
//
// Other free names from the page: DB, showToast, dlog, Uploads.* / Agents.*.

// ── shared helpers (mirrors index.html) ──────────────────
const DEFAULT_CHECKLISTS_ADMIN = {
  open: [
    {section:'1 | Store Environment', items:['Turn on AC, lighting, music, and all TV screens','Morning clean — sweep/mop floors and high-traffic areas; remove rubbish','Check cleaning products are stocked and accessible. If any equipment needs replacing, raise a Tazkera ticket.']},
    {section:'2 | Security & Safety', items:['Check doors and windows for damage','Check fire exits are clear and unlocked','Sign attendance log','Confirm safe balance matches previous night']},
    {section:'3 | Technology & Systems', items:['Check ToyTalk for messages and outstanding actions','Check e-mail, news, tasks and surveys on ToyTalk','Log into CUBES and start day on POS — confirm system is working','Test handheld devices, home delivery app, scanner, card payment terminals, and printers','Check receipt paper — replace if needed','Check delivery app — confirm no outstanding orders from previous day']},
    {section:'4 | Financial Setup', items:['Count safe, petty cash, and opening float — report any variance immediately','Visit bank if low on small denominations','Complete DSR (Daily Sales Reconciliation) — log opening cash total','Put cash in till and complete cash opening form','Note till limit — drop cash to safe during shift when limit is reached']},
    {section:'5 | Morning Floor Walk', items:['Confirm One Pager is actioned — displays, bulk stacks, and clip strips in place','Ensure all demonstration lines are working — replace batteries if needed','Fill empty shelves from stockroom','Complete price sweep (missing SELs) and price integrity check (3 bays) — log both','Action any overnight price changes from ToyTalk','Verify promotional displays are current — check WOW items are correctly displayed']},
    {section:'6 | Team Readiness', items:["Check day rota and confirm live link reflects today's schedule",'All staff in correct uniform with name badges','Conduct team briefing — daily targets, promotions, and priorities','Confirm staff schedules and break rotations','Open store doors on time']}
  ],
  close: [
    {section:'1 | Financial Close', items:['Confirm till limit drops were completed during shift — document any gaps. Max 2000 AED on site at any time.','Count safe and petty cash — report any variances immediately. Max 500 AED in petty cash.','Count till cash and record total','Complete Z report on card payment device','Prepare bank deposit, complete deposit slip (Excel file), and send to finance','Log all cash into safe — till must be empty overnight','Complete safe log book','Log out of all systems, lock till, and secure all payment terminals']},
    {section:'2 | Store Presentation', items:['Face up the store — tidy all shelves, straighten merchandise, reset promotional displays','Ensure till area is clean and tidy — replenish gift wrapping station for next day','Organise demonstration areas for tomorrow','Clean and mop floor, empty all waste bins, clean customer touchpoints and surfaces']},
    {section:'3 | Stock Management', items:['Fill empty spaces on shelves from stockroom — bring priority stock to sales floor','Check top 10 bestselling items — flag any gaps to Line Manager','Check and close down all delivery app orders','Stockroom walk — aisles clear, stock stored safely, no product on floor']},
    {section:'4 | Technology & Equipment', items:['Shut down all devices properly — do not leave on sleep mode. POS terminals should remain on overnight.','Ensure all devices on charge overnight — including PDA and pricing gun','Put rechargeable batteries on charge']},
    {section:'5 | Security & Safety', items:['Check all areas — ensure all customers have left the store','Confirm all staff have signed out and left the store','Check all demo toys work and replace batteries','Process any faulty stock (RFS procedure) — remove damaged products from sales floor','Complete security walk-through','Turn off lights, music system, and non-essential AC','Lock all doors and windows — activate security system']},
    {section:'6 | Administrative Tasks', items:['Check ToyTalk for messages and outstanding actions','Write handover for morning shift and send EOD e-mail','Complete incident logbook if required','Update team communication board and file daily paperwork']}
  ]
};

function adminGetTemplate(type) {
  // First: check loaded DB templates (the new multi-template system)
  if (Array.isArray(clTemplates)) {
    const found = clTemplates.find(t => t.type === type);
    if (found && Array.isArray(found.items)) return found.items;
  }
  // Fallback: legacy localStorage override
  try {
    const s = localStorage.getItem('elc_checklist_template_' + type);
    if (s) return JSON.parse(s);
  } catch(e) {}
  // Last resort: hardcoded defaults (only exist for 'open' / 'close')
  if (DEFAULT_CHECKLISTS_ADMIN[type]) return JSON.parse(JSON.stringify(DEFAULT_CHECKLISTS_ADMIN[type]));
  return [];
}

// Template state (multi-template system) — declared early so adminGetTemplate can reference it

function adminGetAllSubmissions() {
  const results = [];
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(k => {
      if (!k.startsWith('elc_cl_')) return;
      try {
        const rec = JSON.parse(localStorage.getItem(k) || '');
        if (!rec || typeof rec !== 'object') return;
        // Ensure required fields exist — patch from key if missing
        if (!rec.store || !rec.date || !rec.type) {
          // Key format: elc_cl_{store_with_underscores}_{YYYY-MM-DD}_{type}
          const withoutPrefix = k.replace('elc_cl_', '');
          const lastU = withoutPrefix.lastIndexOf('_');
          const secondU = withoutPrefix.lastIndexOf('_', lastU - 1);
          // date is secondU+1 to lastU, type is after lastU
          const possibleDate = withoutPrefix.slice(secondU + 1, lastU);
          const possibleType = withoutPrefix.slice(lastU + 1);
          const possibleStore = withoutPrefix.slice(0, secondU).replace(/_/g, ' ');
          if (!rec.date)  rec.date  = possibleDate;
          if (!rec.type)  rec.type  = possibleType;
          if (!rec.store) rec.store = possibleStore;
        }
        if (!rec.score) rec.score = 0;
        if (!rec.total) rec.total = 1;
        if (!rec.submittedAt) rec.submittedAt = '—';
        if (!rec.staffName)   rec.staffName   = 'Unknown';
        if (!rec.answers)     rec.answers     = {};
        results.push(rec);
      } catch(e) {}
    });
  } catch(e) {}
  return results.sort((a, b) => {
    const da = (a.date || '') + (a.submittedAt || '');
    const db = (b.date || '') + (b.submittedAt || '');
    return db.localeCompare(da);
  });
}

// Seed demo submissions so the tab has data to show in dev/test
function seedDemoChecklistData() {
  const key = 'elc_cl_demo_seeded';
  if (localStorage.getItem(key)) return; // already seeded
  const stores = ['Dubai - Dubai Mall', 'Dubai - Dubai Hills', 'Abu Dhabi - Marina Mall'];
  const today = new Date();
  stores.forEach((store, si) => {
    for (let d = 0; d < 7; d++) {
      const dt = new Date(today); dt.setDate(dt.getDate() - d);
      const dateStr = localDateStr(dt);
      ['open','close'].forEach(type => {
        const tmpl = DEFAULT_CHECKLISTS_ADMIN[type];
        const answers = {};
        let idx = 0;
        tmpl.forEach(sec => {
          sec.items.forEach(() => {
            answers[idx] = {answer: Math.random() > 0.1 ? 'yes' : 'no', comment: ''};
            idx++;
          });
        });
        const total = idx;
        const score = Object.values(answers).filter(a => a.answer === 'yes').length;
        const storeKey = 'elc_cl_' + store.replace(/\s+/g,'_') + '_' + dateStr + '_' + type;
        if (!localStorage.getItem(storeKey)) {
          localStorage.setItem(storeKey, JSON.stringify({
            store, date: dateStr, type,
            staffName: ['Sample Person','Alex Example','Jo Example'][si % 3],
            submittedAt: type === 'open' ? '09:' + String(15 + si * 3).padStart(2,'0') : '22:' + String(10 + si * 2).padStart(2,'0'),
            answers, score, total
          }));
        }
      });
    }
  });
  localStorage.setItem(key, '1');
}

// One-time cleanup of the old demo seed that leaked into the PROD History tab —
// the fake submissions (under three real staff names) that the Ops
// manager spotted "in stores they're not in" (2 Jun 2026). Removes ONLY entries
// authored by the demo placeholder names, so any real cached data is untouched.
function purgeDemoChecklistSeed() {
  try {
    if (localStorage.getItem('elc_cl_demo_purged_v1')) return;
    var demoNames = ['Sample Person', 'Alex Example', 'Jo Example'];
    for (var i = localStorage.length - 1; i >= 0; i--) {
      var k = localStorage.key(i);
      if (!k || k.indexOf('elc_cl_') !== 0 || k === 'elc_cl_demo_seeded' || k === 'elc_cl_demo_purged_v1') continue;
      try {
        var v = JSON.parse(localStorage.getItem(k) || '{}');
        if (v && demoNames.indexOf(v.staffName) !== -1) localStorage.removeItem(k);
      } catch (e) { /* not JSON — leave it */ }
    }
    localStorage.removeItem('elc_cl_demo_seeded');
    localStorage.setItem('elc_cl_demo_purged_v1', '1');
  } catch (e) { /* localStorage unavailable — nothing to do */ }
}

// ── sub-tab switching ─────────────────────────────────────
function switchClAdminTab(tab) {
  document.getElementById('clAdminHistory').style.display    = tab === 'history'    ? 'block' : 'none';
  const comp = document.getElementById('clAdminCompliance');
  if (comp) comp.style.display = tab === 'compliance' ? 'block' : 'none';
  document.getElementById('clAdminTemplate').style.display   = tab === 'template'   ? 'block' : 'none';
  document.getElementById('clAdminTabHistory').className    = tab === 'history'    ? 'btn btn-green' : 'btn btn-outline';
  const compBtn = document.getElementById('clAdminTabCompliance');
  if (compBtn) compBtn.className = tab === 'compliance' ? 'btn btn-green' : 'btn btn-outline';
  document.getElementById('clAdminTabTemplate').className   = tab === 'template'   ? 'btn btn-green' : 'btn btn-outline';
  if (tab === 'history') Uploads.checklists.renderClHistory();
  if (tab === 'compliance') initClAdminCompliance();
  if (tab === 'template') Uploads.checklists.loadClTemplates();
}

// ── populate store filter on page load ───────────────────
function initClAdminPage() {
  // Demo seed is for dev/staging ONLY. In prod it was polluting the History tab
  // with fake submissions (under real staff names), so prod gets a one-time purge.
  var _clHost = (location.hostname || '');
  if (_clHost.indexOf('staging') !== -1 || _clHost.indexOf('localhost') !== -1 || _clHost === '127.0.0.1') {
    seedDemoChecklistData(); // demo data for History/Compliance tabs (non-prod only)
  } else {
    purgeDemoChecklistSeed(); // remove demo submissions that leaked into prod
  }
  // Load templates from DB — needed by History tab to render submitted checklists correctly,
  // and by Template tab to show the list.
  Uploads.checklists.loadClTemplates();
  const sel = document.getElementById('clHistStore');
  if (!sel) return;
  sel.innerHTML = '<option value="">All Stores</option>';
  ALL_STORES.forEach(s => {
    const o = document.createElement('option');
    o.value = s; o.textContent = s; sel.appendChild(o);
  });
  const today = new Date();
  const d = today.getFullYear() + '-' + String(today.getMonth()+1).padStart(2,'0') + '-' + String(today.getDate()).padStart(2,'0');
  document.getElementById('clHistDate').value = d;
  // Default to show last 7 days not just today
  document.getElementById('clHistDate').value = '';
  Uploads.checklists.renderClHistory();
}





async function runAdminClAI() {
  const box = document.getElementById('clAdminAiBox');
  const txt = document.getElementById('clAdminAiText');
  if (!box || !txt) return;   // v136.8: null-safe (the box may not be on screen)
  box.style.display = 'block';
  txt.innerHTML = '<span class="ai-dot"></span><span class="ai-dot"></span><span class="ai-dot"></span> Analysing...';
  const rows = getAdminComplianceData();
  if (!rows.length) { txt.innerHTML = 'No data to analyse.'; return; }
  const byStore = {};
  rows.forEach(r => {
    if (!byStore[r.store]) byStore[r.store]={subs:0,total:0,missed:{}};
    byStore[r.store].subs++; byStore[r.store].total+=r.pct;
    r.missed.forEach(m=>{byStore[r.store].missed[m]=(byStore[r.store].missed[m]||0)+1;});
  });
  const summary = Object.entries(byStore).map(([st,d])=>{
    const avg=Math.round(d.total/d.subs);
    const top=Object.entries(d.missed).sort((a,b)=>b[1]-a[1]).slice(0,3).map(([m,c])=>`${m}(×${c})`).join(', ');
    return `${st.split(' - ').pop()}: avg ${avg}%, ${d.subs} submissions${top?', most missed: '+top:''}`;
  }).join('\n');
  const prompt = `Review checklist compliance for ELC cosmetics stores:\n${summary}\n\nProvide:\n1. Stores with lowest compliance needing attention\n2. Most frequently missed items\n3. Patterns worth noting\n4. Recommended actions for worst 2-3 stores\nBe concise and use bullet points.`;
  try {
    const data = await DB.ai.rota({model:'claude-haiku-4-5-20251001',max_tokens:700,
        system:'You are a retail ops analyst. Be concise and direct.',
        messages:[{role:'user',content:prompt}]});
    const text = (data.content?.[0]?.text)||'';
    txt.innerHTML = escapeHtml(text).replace(/\*\*(.*?)\*\*/g,'<strong>$1</strong>').replace(/^[•\-\*] /gm,'<br>• ').replace(/\n/g,'<br>');   // v136.8 (M3): model text escaped before formatting
  } catch(err) { txt.innerHTML = '❌ '+escapeHtml(err.message); }
}

function clearClHistoryFilters() {
  ['clHistDateFrom','clHistDate','clHistStore','clHistType'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.value = '';
  });
  Uploads.checklists.renderClHistory();
}

// ── history ───────────────────────────────────────────────
// v104.0: renderClHistory → Uploads.checklists.renderClHistory
// ── template editor ───────────────────────────────────────
// (clTemplates and clTplEditing declared earlier — above adminGetTemplate)

// Fetch all templates from DB and render the list
// v104.0: loadClTemplates → Uploads.checklists.loadClTemplates
// v55: Seeds the ELC "Weekly Safety & Compliance" checklist.
// Scheduled on Mon (UAE start-of-week) AND Sun (Qatar start-of-week) via cfg.days[].
// Items exactly mirror Matt's source document; 3 sections, 22 yes/no items plus
// one reference to the separate "Top 10 Stock Check" log type (seeded separately).
// v104.0: seedWeeklySafetyChecklistIfMissing → Uploads.checklists.seedWeeklySafetyChecklistIfMissing
// v104.0: renderClTemplateList → Uploads.checklists.renderClTemplateList
function freqDescribe(freq, cfg) {
  if (freq === 'weekly') {
    const labels = { Mon:'Mon',Tue:'Tue',Wed:'Wed',Thu:'Thu',Fri:'Fri',Sat:'Sat',Sun:'Sun' };
    // v55: support days[] array alongside legacy day string
    if (cfg && Array.isArray(cfg.days) && cfg.days.length) {
      // Preserve calendar order for readability
      const order = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
      const picked = order.filter(d => cfg.days.indexOf(d) !== -1).map(d => labels[d]);
      return 'Weekly — ' + picked.join(' & ');
    }
    const day = (cfg && cfg.day) || 'Mon';
    const label = { Mon:'Monday',Tue:'Tuesday',Wed:'Wednesday',Thu:'Thursday',Fri:'Friday',Sat:'Saturday',Sun:'Sunday' }[day] || day;
    return 'Weekly — ' + label;
  }
  if (freq === 'monthly') {
    const date = (cfg && cfg.date) || 1;
    const suffix = (d => { const m = d % 10, n = d % 100; if (m===1&&n!==11) return 'st'; if (m===2&&n!==12) return 'nd'; if (m===3&&n!==13) return 'rd'; return 'th'; })(date);
    return 'Monthly — ' + date + suffix;
  }
  return 'Daily';
}

function newClTemplate() {
  clTplEditing = {
    type: 'custom_' + Date.now(), // unique type key
    name: '',
    frequency: 'daily',
    frequency_config: {},
    active: true,
    required: true,
    sort_order: (clTemplates.length || 0) + 1,
    items: [{ section: 'Section 1', items: [''] }],
    _isNew: true,
  };
  showClTemplateEditor();
}

function editClTemplate(type) {
  const t = clTemplates.find(x => x.type === type);
  if (!t) return;
  // Clone so edits don't mutate the list until save
  clTplEditing = JSON.parse(JSON.stringify({
    type: t.type,
    name: t.name || t.type,
    frequency: t.frequency || 'daily',
    frequency_config: t.frequency_config || {},
    active: t.active !== false,
    required: t.required !== false,
    sort_order: t.sort_order || 0,
    items: Array.isArray(t.items) ? t.items : [],
  }));
  showClTemplateEditor();
}

function showClTemplateEditor() {
  if (!clTplEditing) return;
  document.getElementById('clTemplateEditorCard').style.display = 'block';
  document.getElementById('clTplTitle').textContent = clTplEditing._isNew ? 'New Checklist' : 'Edit: ' + clTplEditing.name;
  document.getElementById('clTplName').value = clTplEditing.name || '';
  document.getElementById('clTplFrequency').value = clTplEditing.frequency || 'daily';
  document.getElementById('clTplActive').checked = clTplEditing.active !== false;
  document.getElementById('clTplRequired').checked = clTplEditing.required !== false;
  const cfg = clTplEditing.frequency_config || {};
  // v55: hydrate multi-day checkboxes. Supports both new days[] array and legacy day string.
  // If freshly-created (_isNew) and no cfg yet, leave all unchecked; user picks.
  const selectedDays = Array.isArray(cfg.days) && cfg.days.length
    ? cfg.days
    : (cfg.day ? [cfg.day] : []);
  document.querySelectorAll('.cl-tpl-freq-day').forEach(cb => {
    cb.checked = selectedDays.indexOf(cb.value) !== -1;
  });
  document.getElementById('clTplFreqDate').value = cfg.date || 1;
  onClTplFreqChange();
  Uploads.checklists.renderClTemplateEditor();
  document.getElementById('clTemplateEditorCard').scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function cancelClTplEdit() {
  clTplEditing = null;
  document.getElementById('clTemplateEditorCard').style.display = 'none';
}

function onClTplFreqChange() {
  const freq = document.getElementById('clTplFrequency').value;
  document.getElementById('clTplFreqDayWrap').style.display  = freq === 'weekly'  ? 'block' : 'none';
  document.getElementById('clTplFreqDateWrap').style.display = freq === 'monthly' ? 'block' : 'none';
}

// v104.0: renderClTemplateEditor → Uploads.checklists.renderClTemplateEditor
function addClSection() {
  if (!clTplEditing) return;
  clTplEditing.items = clTplEditing.items || [];
  clTplEditing.items.push({ section: 'New Section', items: [''] });
  Uploads.checklists.renderClTemplateEditor();
}

// v104.0: saveClTemplate → Uploads.checklists.saveClTemplate
// v104.0: toggleClTemplateActive → Uploads.checklists.toggleClTemplateActive
// v104.0: deleteClTemplate → Uploads.checklists.deleteClTemplate

async function initLogsAdminPage() {
  await Uploads.logs.loadLogTemplatesAdmin();
  // Populate type filter dropdown
  const typeSel = document.getElementById('logsAdminTypeFilter');
  if (!typeSel) return;   // v136.8: null-safe (the box may not be on screen)
  typeSel.innerHTML = '';
  if (!logTemplatesAdmin.length) {
    typeSel.innerHTML = '<option value="">No log types</option>';
  } else {
    logTemplatesAdmin.forEach(t => {
      const o = document.createElement('option');
      o.value = t.type; o.textContent = (t.icon || '📝') + ' ' + t.name;
      typeSel.appendChild(o);
    });
  }
  // Populate store dropdown
  const storeSel = document.getElementById('logsAdminStoreFilter');
  if (storeSel.options.length <= 1) {
    ALL_STORES.forEach(s => {
      const o = document.createElement('option');
      o.value = s; o.textContent = s; storeSel.appendChild(o);
    });
  }
  // Default dates: last 30 days to today
  const fmt = d => d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0');
  const from = new Date(); from.setDate(from.getDate() - 30);
  const fromEl = document.getElementById('logsAdminFrom');
  const toEl = document.getElementById('logsAdminTo');
  if (fromEl && !fromEl.value) fromEl.value = fmt(from);
  if (toEl && !toEl.value) toEl.value = fmt(new Date());

  Uploads.logs.renderLogsAdminEntries();
}

// v104.0: loadLogTemplatesAdmin → Uploads.logs.loadLogTemplatesAdmin
// v55: Seeds the "Top 10 Stock Check" log type used alongside the weekly
// Safety & Compliance checklist. Six fields mirror the source document's
// grid (Rank, Item, SKU, Expected, Actual, Action). Store managers file
// one entry per item; the weekly checklist item just reminds them to do it.
async function seedTop10StockLogIfMissing() {
  const TYPE = 'top10_stock_check';
  if (logTemplatesAdmin.find(t => t.type === TYPE)) return;
  const payload = {
    type: TYPE,
    name: 'Top 10 Stock Check',
    icon: '📦',
    description: 'Weekly bestseller stock audit. Submit one entry per item. Report variances to Area Manager and Head Office immediately.',
    fields: [
      { label: 'Rank',          key: 'rank',          type: 'number',   required: true  },
      { label: 'Item Name',     key: 'item_name',     type: 'text',     required: true  },
      { label: 'SKU',           key: 'sku',           type: 'text',     required: true  },
      { label: 'Expected Stock',key: 'expected',      type: 'number',   required: true  },
      { label: 'Actual Stock',  key: 'actual',        type: 'number',   required: true  },
      { label: 'Action Required',key:'action_required',type:'textarea', required: false },
    ],
    active: true,
    required: true,          // weekly MANDATORY (Matt, v125.31)
    frequency: 'weekly',     // staff Checks shows it on Mondays; compliance expects 1/week
    sort_order: (logTemplatesAdmin.length || 0) + 10,
  };
  await DB.logTemplates.save(payload);
  logTemplatesAdmin.push(payload);
  console.log('[seed] Created "Top 10 Stock Check" log type (type=' + TYPE + ')');
}

function switchLogsAdminTab(tab) {
  document.getElementById('logsAdminEntries').style.display   = tab === 'entries'   ? 'block' : 'none';
  document.getElementById('logsAdminTemplates').style.display = tab === 'templates' ? 'block' : 'none';
  document.getElementById('logsAdminRetention').style.display = tab === 'retention' ? 'block' : 'none';
  document.getElementById('logsAdminTabEntries').className    = tab === 'entries'   ? 'btn btn-green' : 'btn btn-outline';
  document.getElementById('logsAdminTabTemplates').className  = tab === 'templates' ? 'btn btn-green' : 'btn btn-outline';
  document.getElementById('logsAdminTabRetention').className  = tab === 'retention' ? 'btn btn-green' : 'btn btn-outline';
  if (tab === 'entries')   Uploads.logs.renderLogsAdminEntries();
  if (tab === 'templates') Uploads.logs.renderLogTemplateList();
}

// ── DATA RETENTION ────────────────────────────────────────────────
async function runManualPurge() {
  const daysInput = document.getElementById('purgeDays');
  const days = parseInt(daysInput.value, 10);
  // v132.84: the Worker refuses anything under 365 days (and is Head Office only).
  if (isNaN(days) || days < 365) {
    showToast('Retention period must be at least 365 days', 'error');
    return;
  }
  if (!(await (window.uiConfirm || function (m) { return Promise.resolve(confirm(m)); })('This will permanently delete ALL checks and store logs older than ' + days + ' days (clock records are kept for 7 years). This cannot be undone. Continue?', { danger: true, confirmLabel: 'Delete' }))) return;

  const resultEl = document.getElementById('purgeResult');
  if (!resultEl) return;   // v136.8: null-safe (the box may not be on screen)
  resultEl.style.display = 'block';
  resultEl.innerHTML = '<div style="padding:14px;background:var(--surface-2);border-radius:10px;font-size:13px;color:var(--sub)">⏳ Purging…</div>';

  try {
    // Call the worker endpoint directly (DB helper doesn't have a purge method)
    const token = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = 'Bearer ' + token;

    // v80.14: was hardcoded to the production worker URL, which meant
    // clicking "Purge" on staging would hit production and delete real
    // data with no warning. Now uses DB.workerUrl() like every other
    // admin call so staging-stays-on-staging.
    const workerBase = DB.workerUrl();
    const res = await fetch(workerBase + '/admin/purge-old-data?days=' + days, {
      method: 'POST',
      headers: headers,
    });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const data = await res.json();

    const d = data.deleted || {};
    resultEl.innerHTML =
      '<div style="padding:14px;background:var(--ok-bg);border:1px solid var(--ok-border);border-radius:10px;font-size:13px;color:var(--ok-deep)">' +
        '<strong>✅ Purge complete</strong><br>' +
        'Cutoff date: ' + (data.cutoff || '?') + '<br>' +
        'Deleted: ' + (d.checklists || 0) + ' checklists · ' + (d.store_logs || 0) + ' log entries · ' + (d.clock_log || 0) + ' clock records<br>' +
        '<strong>Total: ' + (data.total_deleted || 0) + ' rows removed</strong>' +
      '</div>';
    showToast('Purge complete: ' + (data.total_deleted || 0) + ' rows deleted', 'success');
  } catch(e) {
    resultEl.innerHTML =
      '<div style="padding:14px;background:var(--arota-bad-pale);border:1px solid var(--arota-bad-border);border-radius:10px;font-size:13px;color:var(--danger-deep)">' +
        '<strong>❌ Purge failed:</strong> ' + (e.message || 'unknown error') +
      '</div>';
    showToast('Purge failed: ' + (e.message || 'unknown'), 'error');
  }
}

function resetLogsFilters() {
  document.getElementById('logsAdminStoreFilter').value = '';
  const fmt = d => d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0');
  const from = new Date(); from.setDate(from.getDate() - 30);
  document.getElementById('logsAdminFrom').value = fmt(from);
  document.getElementById('logsAdminTo').value = fmt(new Date());
  Uploads.logs.renderLogsAdminEntries();
}

// v104.0: renderLogsAdminEntries → Uploads.logs.renderLogsAdminEntries
function downloadLogsCSV() {
  if (!logEntriesAdmin.length) { showToast('No data — apply filters first', 'error'); return; }
  const logType = document.getElementById('logsAdminTypeFilter').value;
  const tmpl = logTemplatesAdmin.find(t => t.type === logType);
  if (!tmpl) return;
  const fields = Array.isArray(tmpl.fields) ? tmpl.fields : [];

  const head = ['Date','Time','Store','Staff'];
  fields.forEach(f => head.push(f.label));
  head.push('Notes');

  const lines = [head.map(csvCell).join(',')];
  logEntriesAdmin.forEach(e => {
    const row = [
      e.entry_date || '',
      e.entry_time ? String(e.entry_time).slice(0,5) : '',
      e.store || '',
      e.staff_name || e.created_by || '',
    ];
    fields.forEach(f => row.push((e.data && e.data[f.key] != null) ? e.data[f.key] : ''));
    row.push(e.notes || '');
    lines.push(row.map(csvCell).join(','));
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = (tmpl.name || logType).replace(/\s+/g,'_') + '_' + todayDateStr() + '.csv';
  a.click();
  showToast('CSV downloaded', 'success');
}

function csvCell(v) {
  const s = String(v == null ? '' : v);
  if (s.indexOf(',') > -1 || s.indexOf('"') > -1 || s.indexOf('\n') > -1) {
    return '"' + s.replace(/"/g,'""') + '"';
  }
  return s;
}

// ── Template editor ───────────────────────────────────────

// v104.0: renderLogTemplateList → Uploads.logs.renderLogTemplateList
function newLogTemplate() {
  logTplEditing = {
    type: 'log_' + Date.now(),
    name: '',
    icon: '📝',
    description: '',
    fields: [],
    active: true,
    required: true,
    frequency: 'daily',
    sort_order: (logTemplatesAdmin.length || 0) + 1,
    _isNew: true,
  };
  showLogTemplateEditor();
}

function editLogTemplate(type) {
  const t = logTemplatesAdmin.find(x => x.type === type);
  if (!t) return;
  logTplEditing = JSON.parse(JSON.stringify({
    type: t.type,
    name: t.name || t.type,
    icon: t.icon || '📝',
    description: t.description || '',
    fields: Array.isArray(t.fields) ? t.fields : [],
    active: t.active !== false,
    required: t.required !== false,
    frequency: t.frequency || 'daily',
    sort_order: t.sort_order || 0,
  }));
  showLogTemplateEditor();
}

function showLogTemplateEditor() {
  if (!logTplEditing) return;
  document.getElementById('logTemplateEditorCard').style.display = 'block';
  document.getElementById('logTplTitle').textContent = logTplEditing._isNew ? 'New Log Type' : 'Edit: ' + logTplEditing.name;
  document.getElementById('logTplName').value = logTplEditing.name || '';
  document.getElementById('logTplIcon').value = logTplEditing.icon || '📝';
  document.getElementById('logTplDescription').value = logTplEditing.description || '';
  document.getElementById('logTplActive').checked = logTplEditing.active !== false;
  document.getElementById('logTplRequired').checked = logTplEditing.required !== false;
  var freqSel = document.getElementById('logTplFrequency');
  if (freqSel) freqSel.value = (logTplEditing.frequency === 'weekly') ? 'weekly' : 'daily';
  Uploads.logs.renderLogTemplateFieldsEditor();
  document.getElementById('logTemplateEditorCard').scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function cancelLogTplEdit() {
  logTplEditing = null;
  document.getElementById('logTemplateEditorCard').style.display = 'none';
}

// v104.0: renderLogTemplateFieldsEditor → Uploads.logs.renderLogTemplateFieldsEditor
function addLogTemplateField() {
  if (!logTplEditing) return;
  logTplEditing.fields = logTplEditing.fields || [];
  logTplEditing.fields.push({ label: '', key: '', type: 'text', required: false });
  Uploads.logs.renderLogTemplateFieldsEditor();
}

function removeLogTemplateField(idx) {
  if (!logTplEditing) return;
  logTplEditing.fields.splice(idx, 1);
  Uploads.logs.renderLogTemplateFieldsEditor();
}

// Every moved function stays window-reachable (markup onclick/onchange,
// switchGroupSub's init calls, and onclick strings generated by
// src/uploads/checklists.js + src/uploads/logs.js — e.g. editClTemplate,
// editLogTemplate, removeLogTemplateField — resolve via window).
window.adminGetTemplate = adminGetTemplate;
window.adminGetAllSubmissions = adminGetAllSubmissions;
window.seedDemoChecklistData = seedDemoChecklistData;
window.purgeDemoChecklistSeed = purgeDemoChecklistSeed;
window.switchClAdminTab = switchClAdminTab;
window.initClAdminPage = initClAdminPage;
window.runAdminClAI = runAdminClAI;
window.clearClHistoryFilters = clearClHistoryFilters;
window.freqDescribe = freqDescribe;
window.newClTemplate = newClTemplate;
window.editClTemplate = editClTemplate;
window.showClTemplateEditor = showClTemplateEditor;
window.cancelClTplEdit = cancelClTplEdit;
window.onClTplFreqChange = onClTplFreqChange;
window.addClSection = addClSection;
window.initLogsAdminPage = initLogsAdminPage;
window.seedTop10StockLogIfMissing = seedTop10StockLogIfMissing;
window.switchLogsAdminTab = switchLogsAdminTab;
window.runManualPurge = runManualPurge;
window.resetLogsFilters = resetLogsFilters;
window.downloadLogsCSV = downloadLogsCSV;
window.csvCell = csvCell;
window.newLogTemplate = newLogTemplate;
window.editLogTemplate = editLogTemplate;
window.showLogTemplateEditor = showLogTemplateEditor;
window.cancelLogTplEdit = cancelLogTplEdit;
window.addLogTemplateField = addLogTemplateField;
window.removeLogTemplateField = removeLogTemplateField;
